import { useState, useEffect } from 'react';
import { X, ChevronRight, Building2, Plus, DoorOpen, Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getProperties, createUnit, saveDoorCode, saveMarketingLink } from '@/lib/api';
import { toast } from 'sonner';

const UNIT_SIZES = ['0/1', '1/1', '2/1', '2/2', '3/1', '3/2', '3/3', 'other'];

function StepChip({ label }) {
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600 mr-1.5 mb-1">
      {label}
    </span>
  );
}

export function UnitCreatePanel({ open, onClose, onCreated, defaultPropertyId, properties: propsProp }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);

  const [properties, setProperties] = useState(propsProp || []);
  const [propertyId, setPropertyId] = useState('');
  const [unitNumber, setUnitNumber] = useState('');
  const [unitSize, setUnitSize] = useState('');
  const [unitSizeCustom, setUnitSizeCustom] = useState('');
  const [baseRent, setBaseRent] = useState('');
  const [availabilityStartDate, setAvailabilityStartDate] = useState('');
  const [closeDate, setCloseDate] = useState('');
  const [additionalCosts, setAdditionalCosts] = useState([]);

  // Optional: Door Codes
  const [showDoorCodes, setShowDoorCodes] = useState(false);
  const [doorCodes, setDoorCodes] = useState({
    housekeeping_code: '', guest_code: '', backup_code_1: '', backup_code_2: '',
  });

  // Optional: Marketing Links
  const [showMarketing, setShowMarketing] = useState(false);
  const [marketing, setMarketing] = useState({
    airbnb_link: '', furnished_finder_link: '', photos_link: '',
  });

  useEffect(() => {
    if (open) {
      resetForm();
      if (!propsProp) loadProperties();
    }
  }, [open]);

  // When the panel opens with a default property, auto-advance
  useEffect(() => {
    if (open && defaultPropertyId) {
      setPropertyId(defaultPropertyId);
      setStep(2);
    }
  }, [open, defaultPropertyId]);

  const loadProperties = async () => {
    try {
      const p = await getProperties();
      setProperties(p);
    } catch {}
  };

  const resetForm = () => {
    setStep(1);
    setPropertyId(''); setUnitNumber(''); setUnitSize(''); setUnitSizeCustom('');
    setBaseRent(''); setAvailabilityStartDate(''); setCloseDate('');
    setAdditionalCosts([]);
    setShowDoorCodes(false);
    setDoorCodes({ housekeeping_code: '', guest_code: '', backup_code_1: '', backup_code_2: '' });
    setShowMarketing(false);
    setMarketing({ airbnb_link: '', furnished_finder_link: '', photos_link: '' });
    setSaving(false); setShowDiscard(false);
  };

  const handleClose = () => {
    if (step > 1) setShowDiscard(true);
    else onClose();
  };

  const addCost = () => setAdditionalCosts(prev => [...prev, { name: '', amount: '' }]);
  const updateCost = (idx, field, value) => {
    setAdditionalCosts(prev => {
      const costs = [...prev];
      costs[idx] = { ...costs[idx], [field]: field === 'amount' ? parseFloat(value) || '' : value };
      return costs;
    });
  };
  const removeCost = (idx) => setAdditionalCosts(prev => prev.filter((_, i) => i !== idx));

  const handleCreate = async () => {
    if (!propertyId || !unitNumber.trim() || !unitSize || !baseRent || !availabilityStartDate) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      const created = await createUnit({
        property_id: propertyId,
        unit_number: unitNumber.trim(),
        unit_size: unitSize,
        unit_size_custom: unitSizeCustom,
        base_rent: parseFloat(baseRent),
        availability_start_date: availabilityStartDate,
        close_date: closeDate || null,
        additional_monthly_costs: additionalCosts.filter(c => c.name && c.amount).map(c => ({ name: c.name, amount: parseFloat(c.amount) })),
      });
      const newUnitId = created?.id;

      // Optionally save door codes if user provided any
      const hasAnyCode = Object.values(doorCodes).some(v => (v || '').trim());
      if (newUnitId && hasAnyCode) {
        try {
          await saveDoorCode({
            unit_id: newUnitId,
            property_id: propertyId,
            admin_code: '', admin_code_note: '',
            housekeeping_code: doorCodes.housekeeping_code || '', housekeeping_code_note: '',
            guest_code: doorCodes.guest_code || '', guest_code_note: '',
            backup_code_1: doorCodes.backup_code_1 || '', backup_code_1_note: '',
            backup_code_2: doorCodes.backup_code_2 || '', backup_code_2_note: '',
          });
        } catch {
          toast.error('Unit created, but door codes failed to save');
        }
      }

      // Optionally save marketing links if user provided any
      const hasAnyLink = Object.values(marketing).some(v => (v || '').trim());
      if (newUnitId && hasAnyLink) {
        try {
          await saveMarketingLink({
            unit_id: newUnitId,
            property_id: propertyId,
            airbnb_link: marketing.airbnb_link || '',
            furnished_finder_link: marketing.furnished_finder_link || '',
            photos_link: marketing.photos_link || '',
            additional_links: [],
          });
        } catch {
          toast.error('Unit created, but marketing links failed to save');
        }
      }

      toast.success('Unit created');
      if (onCreated) onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create unit');
    } finally { setSaving(false); }
  };

  const selectedPropName = properties.find(p => p.id === propertyId)?.name;
  const summaryChips = [];
  if (selectedPropName) summaryChips.push(selectedPropName);
  if (unitNumber && step > 2) summaryChips.push(`Unit ${unitNumber}`);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={handleClose} />
      <div
        className="fixed top-0 right-0 h-full w-[420px] max-w-full bg-white z-50 shadow-xl flex flex-col"
        style={{ borderLeft: '0.5px solid #e5e7eb', animation: 'slideInRight 200ms ease-out' }}
        data-testid="unit-create-panel"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-[15px] font-semibold text-gray-900">New Unit</h2>
          <button onClick={handleClose} className="p-1 rounded-md hover:bg-gray-100" data-testid="unit-create-close">
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        {showDiscard && (
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
            <span className="text-[13px] text-amber-700">Discard changes?</span>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowDiscard(false)}>Keep editing</Button>
              <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => { resetForm(); onClose(); }}>Discard</Button>
            </div>
          </div>
        )}

        {summaryChips.length > 0 && (
          <div className="px-5 pt-3 pb-1 flex flex-wrap">
            {summaryChips.map((c, i) => <StepChip key={i} label={c} />)}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {/* STEP 1: Select Property */}
          <div>
            <p className="text-[13px] font-semibold text-gray-700 mb-3">1. Property</p>
            <Select value={propertyId} onValueChange={v => { setPropertyId(v); if (step < 2) setStep(2); }}>
              <SelectTrigger className="h-9 text-[13px]" data-testid="unit-create-property-select">
                <SelectValue placeholder="Select a property" />
              </SelectTrigger>
              <SelectContent>
                {properties.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}{p.building_id != null ? ` (#${p.building_id})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* STEP 2: Unit Number + Size */}
          {step >= 2 && (
            <div>
              <p className="text-[13px] font-semibold text-gray-700 mb-3">2. Unit details</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-[12px] text-gray-500">Unit Number *</Label>
                  <Input value={unitNumber} onChange={e => setUnitNumber(e.target.value)} placeholder="e.g. 101, 2A" className="h-9 text-[13px] mt-1" data-testid="unit-create-number" />
                </div>
                <div>
                  <Label className="text-[12px] text-gray-500">Unit Size *</Label>
                  <Select value={unitSize} onValueChange={setUnitSize}>
                    <SelectTrigger className="h-9 text-[13px] mt-1" data-testid="unit-create-size">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      {UNIT_SIZES.map(s => <SelectItem key={s} value={s}>{s === 'other' ? 'Other' : s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {unitSize === 'other' && (
                  <div>
                    <Label className="text-[12px] text-gray-500">Custom Size</Label>
                    <Input value={unitSizeCustom} onChange={e => setUnitSizeCustom(e.target.value)} placeholder="e.g. Studio, 4/2" className="h-9 text-[13px] mt-1" />
                  </div>
                )}
              </div>
              {step === 2 && (
                <Button size="sm" className="mt-3 h-8 text-xs" onClick={() => {
                  if (!unitNumber.trim() || !unitSize) { toast.error('Unit number and size are required'); return; }
                  setStep(3);
                }} data-testid="unit-step2-next">
                  Next <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              )}
            </div>
          )}

          {/* STEP 3: Rent + Dates + Costs */}
          {step >= 3 && (
            <div>
              <p className="text-[13px] font-semibold text-gray-700 mb-3">3. Financials & dates</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-[12px] text-gray-500">Base Rent *</Label>
                  <Input type="number" value={baseRent} onChange={e => setBaseRent(e.target.value)} placeholder="Monthly rent amount" className="h-9 text-[13px] mt-1" data-testid="unit-create-rent" />
                </div>
                <div>
                  <Label className="text-[12px] text-gray-500">Availability Start Date *</Label>
                  <Input type="date" value={availabilityStartDate} onChange={e => setAvailabilityStartDate(e.target.value)} className="h-9 text-[13px] mt-1" data-testid="unit-create-avail-date" />
                </div>
                <div>
                  <Label className="text-[12px] text-gray-500">Close Date (optional)</Label>
                  <Input type="date" value={closeDate} onChange={e => setCloseDate(e.target.value)} className="h-9 text-[13px] mt-1" />
                </div>

                {/* Additional Monthly Costs */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-[12px] text-gray-500">Additional Monthly Costs</Label>
                    <button onClick={addCost} className="flex items-center gap-1 text-[11px] text-teal-600 hover:text-teal-700">
                      <Plus className="h-3 w-3" /> Add cost
                    </button>
                  </div>
                  {additionalCosts.map((cost, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 rounded-lg border bg-muted/20">
                      <Input className="flex-1 h-8 text-[12px]" placeholder="Cost name" value={cost.name} onChange={e => updateCost(idx, 'name', e.target.value)} />
                      <Input className="w-24 h-8 text-[12px]" type="number" placeholder="Amount" value={cost.amount} onChange={e => updateCost(idx, 'amount', e.target.value)} />
                      <button onClick={() => removeCost(idx)} className="text-gray-400 hover:text-red-500 text-sm">&times;</button>
                    </div>
                  ))}
                </div>

                {/* Optional: Door Codes */}
                <div className="pt-2 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setShowDoorCodes(v => !v)}
                    className="flex items-center gap-1.5 text-[12px] text-teal-600 hover:text-teal-700 font-medium"
                    data-testid="unit-create-toggle-door-codes"
                  >
                    <DoorOpen className="h-3.5 w-3.5" />
                    {showDoorCodes ? 'Hide' : 'Add'} door codes (optional)
                  </button>
                  {showDoorCodes && (
                    <div className="mt-2 space-y-2 pl-1" data-testid="unit-create-door-codes-section">
                      <div>
                        <Label className="text-[11px] text-gray-500">Housekeeping Code</Label>
                        <Input className="h-8 text-[12px] mt-0.5" value={doorCodes.housekeeping_code} onChange={e => setDoorCodes(d => ({ ...d, housekeeping_code: e.target.value }))} data-testid="unit-create-housekeeping-code" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-gray-500">Guest Code</Label>
                        <Input className="h-8 text-[12px] mt-0.5" value={doorCodes.guest_code} onChange={e => setDoorCodes(d => ({ ...d, guest_code: e.target.value }))} data-testid="unit-create-guest-code" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[11px] text-gray-500">Backup 1</Label>
                          <Input className="h-8 text-[12px] mt-0.5" value={doorCodes.backup_code_1} onChange={e => setDoorCodes(d => ({ ...d, backup_code_1: e.target.value }))} />
                        </div>
                        <div>
                          <Label className="text-[11px] text-gray-500">Backup 2</Label>
                          <Input className="h-8 text-[12px] mt-0.5" value={doorCodes.backup_code_2} onChange={e => setDoorCodes(d => ({ ...d, backup_code_2: e.target.value }))} />
                        </div>
                      </div>
                      <p className="text-[10px] text-gray-400">Admin code is PIN-protected — set it later from the unit.</p>
                    </div>
                  )}
                </div>

                {/* Optional: Marketing Links */}
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={() => setShowMarketing(v => !v)}
                    className="flex items-center gap-1.5 text-[12px] text-teal-600 hover:text-teal-700 font-medium"
                    data-testid="unit-create-toggle-marketing"
                  >
                    <Megaphone className="h-3.5 w-3.5" />
                    {showMarketing ? 'Hide' : 'Add'} marketing links (optional)
                  </button>
                  {showMarketing && (
                    <div className="mt-2 space-y-2 pl-1" data-testid="unit-create-marketing-section">
                      <div>
                        <Label className="text-[11px] text-gray-500">Airbnb URL</Label>
                        <Input className="h-8 text-[12px] mt-0.5" value={marketing.airbnb_link} onChange={e => setMarketing(m => ({ ...m, airbnb_link: e.target.value }))} placeholder="https://airbnb.com/rooms/..." data-testid="unit-create-airbnb-link" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-gray-500">Furnished Finder URL</Label>
                        <Input className="h-8 text-[12px] mt-0.5" value={marketing.furnished_finder_link} onChange={e => setMarketing(m => ({ ...m, furnished_finder_link: e.target.value }))} placeholder="https://furnishedfinder.com/..." data-testid="unit-create-ff-link" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-gray-500">Photos URL</Label>
                        <Input className="h-8 text-[12px] mt-0.5" value={marketing.photos_link} onChange={e => setMarketing(m => ({ ...m, photos_link: e.target.value }))} placeholder="https://drive.google.com/..." />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {step >= 3 && (
          <div className="px-5 py-3 border-t bg-gray-50">
            <Button className="w-full h-9 text-[13px]" disabled={!propertyId || !unitNumber.trim() || !unitSize || !baseRent || !availabilityStartDate || saving} onClick={handleCreate} data-testid="unit-create-submit">
              {saving ? 'Creating...' : 'Create unit'}
            </Button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}
